import React, { useContext, useEffect, useState } from 'react';
import { Theme, ThemeProvider } from '@mui/material/styles';
import { createTheme, CssBaseline } from '@mui/material';
import { baseThemePalette, buildTheme } from '@/components/shared/Theme/baseTheme';
import darkThemePalette from '@/components/shared/Theme/darkTheme';
import { ApiProvider } from '@/provider/ApiProvider';
import { useUserData } from '@/hooks/useUserData';
import {
  ThemeComponentState,
  FontType,
  ThemeComponentProps,
  UpdateThemeContextType,
  FontResponseType,
} from '@/components/shared/Theme/types';
import { mapFontResponseToState } from '@/utils/typography';
import globalStyles from '@/components/shared/Theme/globalStyles';

export const defaultFont: FontType = {
  id: 1,
  name: 'Roboto',
  fontFamily: 'Roboto',
  url: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2',
  format: 'woff2',
  isSystemDefault: true,
  isPublic: false,
};

// eslint-disable-next-line
export const ThemeComponentContext = React.createContext<
  | (ThemeComponentState & {
      updateThemeContext: UpdateThemeContextType;
    })
  | null
>(null);

export const ThemeComponentProvider: React.FC<ThemeComponentProps> = ({ children }) => {
  const [userType, setUserType] = useState<number>(useUserData().userData.type);

  const baseTheme = createTheme({ palette: baseThemePalette });
  const userCanViewThemeConfigurator =
    [2, 3, 4].includes(userType) && ApiProvider.permission('view_theme_screen');
  const [state, setState] = useState<ThemeComponentState>({
    themeName: 'default',
    systemFontFamily: defaultFont,
    systemFont: defaultFont,
    isSystemFont: true,
    baseTheme,
    darkTheme: createTheme(baseTheme, { palette: darkThemePalette }),
    customTheme: baseTheme,
    previewTabName: 'default',
    availableFonts: [defaultFont],
    forceUpdate: 1,
  });

  useEffect(() => {
    const {
      userData: { type },
    } = useUserData();

    if (typeof type === 'number' && type !== userType) setUserType(type);
  }, [state.forceUpdate]);

  /**
   * API Calls.
   */
  useEffect(() => {
    if (!userType) return;

    const isExpectedAccessError = (response: {
      code?: number;
      status?: number;
      message?: string;
    }) => {
      const statusCode = Number(response?.code ?? response?.status ?? NaN);
      const message = String(response?.message ?? '')
        .trim()
        .toLowerCase();

      return statusCode === 403 || message.includes('does not have access');
    };

    (async () => {
      let newFont: FontType | null = null;
      let newAvailableFonts: Array<FontType> | null = null;
      let newThemeName: string | null = null;
      let newCustomTheme: Theme | null = null;

      /**
       * Get currently selected user font.
       */
      try {
        const response = await ApiProvider.webserviceGet('UserFont');

        if (response.success === false) {
          if (!isExpectedAccessError(response)) {
            throw new Error('Cannot fetch selected user font!');
          }
        } else if (response.success === true) {
          const font = mapFontResponseToState(response.data);

          if (font.name !== state.systemFont.name) {
            newFont = font;
          }
        }
        /* eslint-disable  @typescript-eslint/no-explicit-any */
      } catch (error: any) {
        ApiProvider.globalToastMessage(error.message, 5000, 'error');
      }

      if (userCanViewThemeConfigurator) {
        /**
         * Fetch the list of available fonts.
         */
        try {
          const response = await ApiProvider.webserviceGet('Font');

          if (response.success === false) {
            if (!isExpectedAccessError(response)) {
              throw new Error('Cannot fetch list of available fonts!');
            }
          } else if (response.success === true) {
            newAvailableFonts = response.data.map((font: FontResponseType) =>
              mapFontResponseToState(font),
            );
          }
          /* eslint-disable  @typescript-eslint/no-explicit-any */
        } catch (error: any) {
          ApiProvider.globalToastMessage(error.message, 5000, 'error');
        }

        /**
         * Fetch the color palette data.
         */
        try {
          const response = await ApiProvider.webserviceGet('ColorPalette');

          if (response.success === false) {
            if (!isExpectedAccessError(response)) {
              throw new Error('Cannot fetch color palette settings!');
            }
          } else if (response.success === true) {
            const { theme, customPalette } = response.data;

            newThemeName = theme;
            newCustomTheme = customPalette ? createTheme({ palette: customPalette }) : baseTheme;
          }
          /* eslint-disable  @typescript-eslint/no-explicit-any */
        } catch (error: any) {
          ApiProvider.globalToastMessage(error.message, 5000, 'error');
        }
      }

      if (newFont || newAvailableFonts || newThemeName || newCustomTheme)
        setState((prevState) => ({
          ...prevState,
          ...(newFont ? { systemFont: newFont } : {}),
          ...(newAvailableFonts ? { availableFonts: newAvailableFonts } : {}),
          ...(newThemeName ? { themeName: newThemeName } : {}),
          ...(newCustomTheme ? { customTheme: newCustomTheme } : {}),
        }));
    })();
  }, [state.forceUpdate, userType]);

  /**
   * Method to update the Storage of the Theme-related data in the provider.
   *
   * @param property Key of the property we want to update.
   * @param newValue New value related to the provided key.
   */
  const updateThemeContext: UpdateThemeContextType = (property, newValue) => {
    setState((prevState) => ({
      ...prevState,
      [property]: newValue,
    }));
  };

  /**
   * Create the current theme based on a Custom palette and a selected Typography value.
   *
   * @param theme Theme name.
   */
  const getCurrentTheme = (theme: string) => {
    const { systemFont } = state;

    switch (theme) {
      case 'dark':
        return buildTheme(darkThemePalette, systemFont);
      case 'custom':
        return buildTheme(state.customTheme.palette, systemFont);
      default:
        return buildTheme(baseThemePalette, systemFont);
    }
  };

  return (
    <ThemeComponentContext.Provider value={{ ...state, updateThemeContext }}>
      <ThemeProvider theme={getCurrentTheme(state.themeName)}>
        <CssBaseline />
        {globalStyles}
        {children}
      </ThemeProvider>
    </ThemeComponentContext.Provider>
  );
};

export const useThemeUI = () => {
  const context = useContext(ThemeComponentContext);

  if (!context) {
    throw new Error('Something went terribly wrong. Please try again.');
  }

  return context;
};
